import { useEffect, useRef, useState } from 'react';
import Video from '@/Components/Media/Video';
import { useAppContext } from '@/Components/Providers/AppProvider';
import { useEventListener } from 'usehooks-ts';
import { useMediaQuery } from '@uidotdev/usehooks';
import { Head } from '@inertiajs/react';

type Props = {}

export default function Landing({}: Props) {
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const isExtraSmall = useMediaQuery('(max-width: 300px)');

    const frameTopLeftRef = useRef<HTMLDivElement | null>(null);
    const frameTopRightRef = useRef<HTMLDivElement | null>(null);
    const frameBottomLeftRef = useRef<HTMLDivElement | null>(null);
    const frameBottomRightRef = useRef<HTMLDivElement | null>(null);

    const [topHeight, setTopHeight] = useState(0);

    const { navigationBarAnchor } = useAppContext();

    useEffect(() => {
        if (navigationBarAnchor?.current) {
            const height = navigationBarAnchor.current.offsetHeight;
            setTopHeight(height);
        }
    }, []);

    useEventListener('resize', () => {
        if (navigationBarAnchor?.current) {
            const height = navigationBarAnchor.current.offsetHeight;
            setTopHeight(height);
        }
    });

    const {
        miniContactMenu: [contactMenuOpen],
        homepageAnchors: { home: middleSection },
    } = useAppContext();

    return <section
            id="landing"
            ref={middleSection}
            className="relative min-h-[820px] w-full flex justify-between items-center"
            style={{
                top: `${topHeight}px`,
                minHeight: `calc(100dvh - ${topHeight}px)`,
            }}
        >
            <Head>
                <link rel="preload" href="https://slothsh-bucket.ams3.cdn.digitaloceanspaces.com/oursharestudios/video/landing-reel-01.mp4" as="video"></link>
            </Head>
            <div className="absolute w-full h-[calc(100dvh-100px)] min-h-[500px] top-0 right-0 grid grid-cols-16 grid-rows-6 z-6"></div>
            <div className="absolute w-full h-[calc(100dvh-100px)] min-h-[500px] top-0 right-0 grid grid-cols-16 grid-rows-6 z-5"
                style={{
                    height: `calc(100dvh - ${topHeight}px + 2px)`,
                }}
            >
                <div ref={frameTopRightRef} className={mc("relative col-span-16 md:col-span-10 row-span-4 border-0 md:border-r-2 border-brand")}>
                    <div className={mc("absolute bottom-0 left-0 w-full h-0 opacity-0 transition-all duration-500 ease-in-out z-8", {
                        'opacity-100 h-full': contactMenuOpen,
                    })}>
                    </div>
                    <Video src="https://slothsh-bucket.ams3.cdn.digitaloceanspaces.com/oursharestudios/video/landing-reel-01.mp4#t=5"
                        width={frameTopLeftRef.current?.getBoundingClientRect().width ?? 0}
                        height={frameTopLeftRef.current?.getBoundingClientRect().height ?? 0}
                        className="object-cover object-center w-full h-full"
                    />
                    <div className={mc("absolute w-full bottom-0 left-0 bg-brand font-title text-brand-light text-left p-2 sm:p-4 md:hidden flex flex-row justify-between items-center translate-y-6 z-15")}>
                        <h2 className={mc("text-2xl md:text-[3rem] font-medium uppercase transition-all duration-250 ease-in-out", { 'hidden': isExtraSmall })}>
                            Our Share
                        </h2>
                        <h2 className={mc("text-lg sm:text-2xl md:text-[3rem] tracking-tight font-medium uppercase transition-all duration-250 ease-in-out")}>
                            A fair share for everyone involved
                        </h2>
                    </div>
                </div>
                <div ref={frameTopRightRef} className={mc("relative col-span-6 row-span-4", { 'hidden': !isDesktop })}>
                    <Video src="https://slothsh-bucket.ams3.cdn.digitaloceanspaces.com/oursharestudios/video/landing-reel-01.mp4#t=12.5"
                        width={frameTopRightRef.current?.getBoundingClientRect().width ?? 0}
                        height={frameTopRightRef.current?.getBoundingClientRect().height ?? 0}
                        className="brightness-90 object-cover object-center w-full h-full"
                    />
                </div>
                <div ref={frameBottomLeftRef} className={mc("relative col-span-6 md:col-span-8 row-span-2 border-y-2 border-brand")}>
                    <div className={mc("absolute top-0 left-0 w-full h-0 opacity-0 transition-all duration-500 ease-in-out z-8", {
                        'opacity-100 h-full': contactMenuOpen,
                    })}>
                    </div>
                    <Video src="https://slothsh-bucket.ams3.cdn.digitaloceanspaces.com/oursharestudios/video/landing-reel-01.mp4#t=20"
                        width={frameBottomLeftRef.current?.getBoundingClientRect().width ?? 0}
                        height={frameBottomLeftRef.current?.getBoundingClientRect().height ?? 0}
                        className="brightness-90 object-cover object-center w-full h-full"
                    />
                </div>
                <div ref={frameBottomRightRef} className="isolate relative col-span-10 md:col-span-8 row-span-2 border-l-2 border-y-2 border-brand overflow-clip">
                    <div className={mc("absolute top-0 right-0 w-0 h-full opacity-0 transition-all duration-500 ease-in-out z-8", {
                        'opacity-100 w-full': contactMenuOpen,
                    })}>
                    </div>
                    <div className="absolute w-full h-full top-0 left-0 font-title text-right p-4 md:p-8 hidden md:flex flex-col justify-end items-end space-y-[-1rem] z-15">
                        <h1 className="uppercase font-bold text-[4rem] md:text-[4rem] transition-all ease-in-out duration-250">Our Share</h1>
                        <h2 className={mc("text-2xl md:text-[3rem]/10 tracking-tight font-medium uppercase transition-all duration-250 ease-in-out")}>
                            A fair share for everyone involved
                        </h2>
                    </div>
                    <Video src="https://slothsh-bucket.ams3.cdn.digitaloceanspaces.com/oursharestudios/video/landing-reel-01.mp4#t=30"
                        width={frameTopLeftRef.current?.getBoundingClientRect().width ?? 0}
                        height={frameTopLeftRef.current?.getBoundingClientRect().height ?? 0}
                        className="brightness-90 md:brightness-40 object-cover object-center w-full h-full"
                    />
                </div>
            </div>
        </section>
}
